import { useState, useEffect, useRef, useCallback } from "react";
import {
  Search as SearchIcon,
  ErrorOutline as AlertCircleIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";

const AdvancedSearchBar = ({
  // Core props
  value = "",
  onChange = () => {},
  onSearch = () => {},
  placeholder = "Search...",

  // Debouncing props
  enableDebounce = true,
  debounceDelay = 500,

  // Loading props
  isLoading = false,
  showLoadingSpinner = true,
  loadingText = "Searching...",

  // Error props
  error = null,
  errorMessage = null,

  // UI props
  className = "",
  disabled = false,
  clearable = true,

  // Additional props
  name = "",
  autoFocus = false,
  maxLength = null,
}) => {
  // Type safety and default value checks
  const safeValue = typeof value === "string" ? value : "";
  const safePlaceholder =
    typeof placeholder === "string" ? placeholder : "Search...";
  const safeName = typeof name === "string" ? name : "";
  const safeClassName = typeof className === "string" ? className : "";
  const safeLoadingText =
    typeof loadingText === "string" ? loadingText : "Searching...";
  const safeDebounceDelay =
    typeof debounceDelay === "number" && debounceDelay > 0
      ? debounceDelay
      : 500;
  const safeMaxLength =
    typeof maxLength === "number" && maxLength > 0 ? maxLength : null;

  // Boolean safety checks
  const safeEnableDebounce = Boolean(enableDebounce);
  const safeIsLoading = Boolean(isLoading);
  const safeShowLoadingSpinner = Boolean(showLoadingSpinner);
  const safeDisabled = Boolean(disabled);
  const safeClearable = Boolean(clearable);
  const safeAutoFocus = Boolean(autoFocus);

  const [searchTerm, setSearchTerm] = useState(safeValue);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  // Safe callback functions with silent error handling
  const safeOnChange = useCallback(
    (event) => {
      try {
        if (typeof onChange === "function") {
          onChange(event);
        }
      } catch (error) {
        // Silent handling - onChange errors are typically not user-facing
      }
    },
    [onChange]
  );

  const safeOnSearch = useCallback(
    (term) => {
      try {
        if (typeof onSearch === "function") {
          onSearch(term);
        }
      } catch (error) {
        // Show user-friendly error for search failures
        toast.error("Search operation failed. Please try again.");
      }
    },
    [onSearch]
  );

  // Update internal state when external value changes
  useEffect(() => {
    setSearchTerm(safeValue);
  }, [safeValue]);

  // Debounced search function with safety checks
  const debouncedSearch = useCallback(
    (term) => {
      try {
        // Ensure term is a string
        const safeTerm = typeof term === "string" ? term : "";

        if (safeEnableDebounce) {
          setIsDebouncing(true);

          // Clear existing timeout with null check
          if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
          }

          // Set new timeout with error handling
          debounceTimeoutRef.current = setTimeout(() => {
            try {
              setIsDebouncing(false);
              safeOnSearch(safeTerm);
            } catch (error) {
              setIsDebouncing(false);
              toast.error("Search operation failed. Please try again.");
            }
          }, safeDebounceDelay);
        } else {
          safeOnSearch(safeTerm);
        }
      } catch (error) {
        setIsDebouncing(false);
        toast.error("Search operation failed. Please try again.");
      }
    },
    [safeEnableDebounce, safeDebounceDelay, safeOnSearch]
  );

  // Handle input change with comprehensive safety checks
  const handleInputChange = useCallback(
    (e) => {
      try {
        // Null check for event and target
        if (!e || !e.target) {
          return; // Silent handling for invalid events
        }

        // Safe value extraction with fallback
        const newValue = e.target.value || "";

        // Ensure newValue is a string before calling trim()
        const safeNewValue = typeof newValue === "string" ? newValue : "";

        setSearchTerm(safeNewValue);

        // Call onChange with error handling
        safeOnChange(e);

        // Trigger debounced search with string safety
        if (safeNewValue.trim()) {
          debouncedSearch(safeNewValue);
        } else {
          // Clear timeout if search term is empty
          if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
            setIsDebouncing(false);
          }
          safeOnSearch("");
        }
      } catch (error) {
        // Silent handling for input change errors
      }
    },
    [debouncedSearch, safeOnChange, safeOnSearch]
  );

  // Handle clear button with safety checks
  const handleClear = useCallback(() => {
    try {
      setSearchTerm("");

      // Create safe event object
      const safeEvent = {
        target: {
          name: safeName,
          value: "",
        },
      };

      safeOnChange(safeEvent);
      safeOnSearch("");

      // Clear timeout with null check
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
        setIsDebouncing(false);
      }

      // Focus input after clearing with null check
      if (inputRef.current && typeof inputRef.current.focus === "function") {
        try {
          inputRef.current.focus();
        } catch (focusError) {
          // Silent handling for focus errors
        }
      }
    } catch (error) {
      // Silent handling for clear operation errors
    }
  }, [safeName, safeOnChange, safeOnSearch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      try {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
      } catch (error) {
        // Silent handling for cleanup errors
      }
    };
  }, []);

  // Determine if we should show loading state with safety checks
  const showLoading = safeIsLoading || (safeEnableDebounce && isDebouncing);

  // Determine if we should show error with safety checks
  const showError = Boolean(error || errorMessage);
  const safeErrorMessage =
    typeof errorMessage === "string"
      ? errorMessage
      : typeof error === "string"
      ? error
      : "An error occurred";

  // Safe search term for rendering
  const safeSearchTerm = typeof searchTerm === "string" ? searchTerm : "";

  return (
    <div className={`relative w-full ${safeClassName}`}>
      {/* Search Icon */}
      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />

      {/* Input Field */}
      <input
        ref={inputRef}
        type="text"
        value={safeSearchTerm}
        onChange={handleInputChange}
        placeholder={safePlaceholder}
        name={safeName}
        disabled={safeDisabled}
        autoFocus={safeAutoFocus}
        maxLength={safeMaxLength}
        className={`
          w-full pl-10 pr-12 py-2 
          border border-gray-300 rounded-lg 
          focus:outline-none focus:ring-2 focus:ring-primaryGreen/20 focus:border-primaryGreen
          disabled:bg-gray-100 disabled:cursor-not-allowed
          ${
            showError
              ? "border-red-400 focus:ring-red-200 focus:border-red-400"
              : ""
          }
          ${showLoading ? "pr-16" : ""}
        `}
      />

      {/* Loading Spinner */}
      {showLoading && safeShowLoadingSpinner && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <div className="h-4 w-4 border-2 border-primaryGreen border-t-transparent rounded-full animate-spin"></div>
          {safeLoadingText && (
            <span className="text-xs text-primaryGreen font-medium hidden sm:inline-block">
              {safeLoadingText}
            </span>
          )}
        </div>
      )}

      {/* Clear Button */}
      {safeClearable && safeSearchTerm && !showLoading && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
          title="Clear search"
        >
          <svg
            className="h-4 w-4 text-gray-400 hover:text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}

      {/* Error Message */}
      {showError && (
        <div className="absolute top-full left-0 w-full mt-1">
          <div className="flex items-center gap-1 text-red-600 text-xs">
            <AlertCircleIcon className="h-3 w-3" />
            <span>{safeErrorMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearchBar;
